import { Colors, actuatorDispositionStyle } from "design/colors";
import {
    FaultCode,
} from "utils/common-types";
import { PlotKey } from "utils/packet.worker";
import { faultCodeToString } from "utils/common";
import { REFRESH_MS } from "hooks/usePlayback";
import { TelemValue } from "cstore/telem";
import { useSingleTelemetryValue } from "hooks/useSingleTelemetryValue";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGripVertical, faXmark } from "@fortawesome/free-solid-svg-icons";
import { Button } from "../Button";
import { usePlaybackSession } from "connections/playbackSession";

export interface IFaultControlWidgetProps {
    close: () => void;
    clearAllFaults: () => void;
}

export interface IFaultControlProps {
    faultBitmapLowerPacketKey: PlotKey;
    faultBitmapUpperPacketKey: PlotKey;
    label?: string;
    hideClearAllFaultsBtn?: true; // Why not simply a required boolean? Backwards compat
}

// Caller must ensure the props are for the correct fault bitmap keys.
export const FaultControl = ( props: IFaultControlProps & IFaultControlWidgetProps ): JSX.Element => {
    const latestFaultBitmapLower: TelemValue | undefined = useSingleTelemetryValue( props.faultBitmapLowerPacketKey, REFRESH_MS );
    const latestFaultBitmapUpper: TelemValue | undefined = useSingleTelemetryValue( props.faultBitmapUpperPacketKey, REFRESH_MS );
    const { playbackSession } = usePlaybackSession();

    if ( typeof latestFaultBitmapLower !== "number" || typeof latestFaultBitmapUpper !== "number" ) {
        return <></>;
    }

    let faults = decodeBitmap( latestFaultBitmapLower ).concat(decodeBitmap( latestFaultBitmapUpper, 32 ) );

    return (
        <div style={{ display: "flex", flex: 1, flexDirection: "column", overflow: "auto" }}>
            <div style={{ display: "flex", width: "100%", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", flex: "column" }}>
                    {/* Drag and drop drag handle */}
                    <FontAwesomeIcon
                        icon={ faGripVertical }
                        color={ Colors.WHITE }
                        style={{ padding: "0px 8px 0px 0px", cursor: "pointer" }}
                        size="lg"
                    />
                    <div style={{ color: Colors.WHITE }}>{ props.label && <span style={actuatorDispositionStyle}>{props.label}</span> } Fault Control</div>
                </div>
                <FontAwesomeIcon
                    icon={ faXmark }
                    color={ Colors.WHITE }
                    style={{ padding: "0px 4px", cursor: "pointer" }}
                    onClick={ props.close }
                    size="lg"
                />
            </div>
            { !playbackSession?.isPlayback && !props.hideClearAllFaultsBtn &&
                <div style={{ display: "flex", width: "100%", justifyContent: "center", alignItems: "center" }}>
                    <Button
                        style={{ marginTop: 4, width: "25%" }}
                        backgroundColor={ Colors.BUTTON_GREEN }
                        textColor={ Colors.WHITE }
                        onClick={ props.clearAllFaults }
                    >
                        Clear All Faults
                    </Button>
                </div>
            }
            <div style={{ display: "flex", flex: 1, fontSize: 32, color: Colors.WHITE, justifyContent: "center", alignItems: "center", overflow: "auto", paddingTop: 30 }}>
                <ul>
                    { faults.length === 0 ? "No faults" : faults.map((s) => <li key={s}>{s}</li>) }
                </ul>
            </div>
        </div>
    );
};

// Returns an array of human-readable fault code strings.
//
// The offset, if present, should be 32 to specify the upper 16 bits.
const decodeBitmap = (bitmap: number, offset?: number): string[] => {
    let codes: string[] = [];
    if (offset === undefined) offset = 0;
    let fc: keyof typeof FaultCode;
    for (fc in FaultCode) {
        if ( bitmap === 0 ) break;
        const n = Number(FaultCode[fc]);
        if ( !isFinite(n) || n < offset ) continue;
        const x = 1 << ( n - offset );
        if ( ( bitmap & x ) > 0 ) {
            codes.push(faultCodeToString(n as FaultCode));
            bitmap -= x;
        }
    }
    return codes;
};
